import { db } from '../firebase'
import { collection, query, orderBy, limit, where, getDocs, addDoc, serverTimestamp, Timestamp } from 'firebase/firestore'

export class MessagesService {
  constructor(customerName) {
    this.customerName = customerName
    this.collectionName = `${customerName}_messages`
  }

  /**
   * Get recent activity messages
   * @param {number} limitCount - Number of messages to fetch (default: 100)
   * @returns {Promise<Array>} Array of message objects
   */
  async getRecentMessages(limitCount = 100) {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      const messagesRef = collection(db, this.collectionName)
      const q = query(
        messagesRef,
        orderBy('dateTime', 'desc'),
        limit(limitCount)
      )
      
      const snapshot = await getDocs(q)
      
      const messages = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        messages.push({
          id: doc.id,
          type: data.Type || data.type || 'Unknown Action',
          source: data.source || 'unknown',
          customer: data.customer,
          deviceId: data.deviceId,
          dateTime: data.dateTime, // Firestore timestamp object
          expires: data.expires,
          ...data // Include any additional fields
        })
      })

      return messages
    } catch (error) {
      console.error('Error fetching recent messages:', error)
      throw error
    }
  }

  /**
   * Get recent activity messages for a specific device
   * @param {string} deviceId - Device ID to filter messages for
   * @param {number} limitCount - Number of messages to fetch (default: 50)
   * @returns {Promise<Array>} Array of message objects for the device
   */
  async getDeviceMessages(deviceId, limitCount = 50) {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      if (!deviceId) {
        throw new Error('Device ID is required')
      }

      console.log(`Getting messages for device: ${deviceId} from collection: ${this.collectionName}`)
      
      const messagesRef = collection(db, this.collectionName)
      
      // Query without orderBy to avoid index requirements, sort manually in JS
      const q = query(
        messagesRef,
        where('deviceId', '==', deviceId),
        limit(limitCount)
      )
      
      const snapshot = await getDocs(q)
      console.log(`Query executed, snapshot size: ${snapshot.size}`)
      
      const messages = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        messages.push({
          id: doc.id,
          type: data.Type || data.type || 'Unknown Action',
          source: data.source || 'unknown',
          customer: data.customer,
          deviceId: data.deviceId,
          dateTime: data.dateTime, // Firestore timestamp object
          expires: data.expires,
          ...data // Include any additional fields
        })
      })

      // Sort manually in JavaScript by dateTime (newest first)
      messages.sort((a, b) => {
        if (!a.dateTime || !b.dateTime) return 0
        return b.dateTime.toDate() - a.dateTime.toDate()
      })

      console.log(`Fetched ${messages.length} messages for device ${deviceId}`)
      return messages
    } catch (error) {
      console.error(`Error fetching messages for device ${deviceId}:`, error)
      throw error
    }
  }

  /**
   * Write a new activity message to the collection
   * @param {Object} messageData - The message data to write
   * @param {string} messageData.type - Type of action performed
   * @param {string} messageData.deviceId - Device ID associated with the action
   * @param {string} [messageData.source] - Source of the action (default: 'webapp')
   * @param {string} [messageData.userId] - User who performed the action (for webapp source)
   * @param {string} [messageData.userEmail] - User email for identification
   * @param {Object} [messageData.additionalData] - Any additional data to store
   * @returns {Promise<string>} Document ID of created message
   */
  async writeMessage({ type, deviceId, source = 'webapp', userId, userEmail, additionalData = {} }) {
    try {
      if (!this.customerName) {
        throw new Error('Customer name is required')
      }

      if (!type || !deviceId) {
        throw new Error('Type and deviceId are required')
      }

      const now = serverTimestamp()
      
      // Calculate expires date (30 days from now)
      const expiresDate = new Date()
      expiresDate.setDate(expiresDate.getDate() + 30)
      
      const messageDoc = {
        Type: type, // Use capital T to match existing format
        source: source, // Allow custom source, default to 'webapp'
        customer: this.customerName,
        deviceId: deviceId,
        dateTime: now,
        expires: Timestamp.fromDate(expiresDate), // 30 days from now for TTL
        // Add user information for webapp actions
        ...(userId && { userId: userId }),
        ...(userEmail && { userEmail: userEmail }),
        // Include any additional data
        ...additionalData
      }

      const messagesRef = collection(db, this.collectionName)
      const docRef = await addDoc(messagesRef, messageDoc)

      console.log(`Activity logged: ${type} for device ${deviceId} by user ${userEmail} (source: ${source})`)
      
      return docRef.id
    } catch (error) {
      console.error('Error writing activity message:', error)
      throw error
    }
  }

  /**
   * Format Firestore timestamp to human-readable relative time
   * @param {Object} firestoreTimestamp - Firestore timestamp object
   * @returns {string} Formatted relative time (e.g., "2 min ago")
   */
  static formatRelativeTime(firestoreTimestamp) {
    try {
      if (!firestoreTimestamp || !firestoreTimestamp.toDate) {
        return 'Unknown time'
      }

      const messageDate = firestoreTimestamp.toDate()
      const now = new Date()
      const diffMs = now - messageDate
      const diffMinutes = Math.floor(diffMs / (1000 * 60))
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      if (diffMinutes < 1) {
        return 'Just now'
      } else if (diffMinutes < 60) {
        return `${diffMinutes} min ago`
      } else if (diffHours < 24) {
        return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
      } else if (diffDays < 7) {
        return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
      } else {
        return messageDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: messageDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
        })
      }
    } catch (error) {
      console.error('Error formatting timestamp:', error)
      return 'Unknown time'
    }
  }

  /**
   * Format Firestore timestamp to actual date and time
   * @param {Object} firestoreTimestamp - Firestore timestamp object
   * @returns {string} Formatted actual timestamp (e.g., "Jan 15, 2025 at 2:30 PM")
   */
  static formatActualTime(firestoreTimestamp) {
    try {
      if (!firestoreTimestamp || !firestoreTimestamp.toDate) {
        return 'Unknown time'
      }

      const messageDate = firestoreTimestamp.toDate()
      const now = new Date()

      // Format date
      const dateOptions = {
        month: 'short',
        day: 'numeric',
        year: messageDate.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      }
      
      // Format time
      const timeOptions = {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }

      const formattedDate = messageDate.toLocaleDateString('en-US', dateOptions)
      const formattedTime = messageDate.toLocaleTimeString('en-US', timeOptions)

      // For today's messages, just show time
      if (messageDate.toDateString() === now.toDateString()) {
        return `Today at ${formattedTime}`
      }

      return `${formattedDate} at ${formattedTime}`
    } catch (error) {
      console.error('Error formatting actual timestamp:', error)
      return 'Unknown time'
    }
  }

  /**
   * Get icon class for different action types
   * @param {string} actionType - The type of action performed
   * @returns {string} FontAwesome icon class
   */
  static getActionIcon(actionType) {
    const iconMap = {
      'Layout updated': 'fas fa-th-large text-primary',
      'Payment gateway': 'fas fa-credit-card text-success', 
      'Service PIN changed': 'fas fa-key text-warning',
      'Inventory updated': 'fas fa-boxes text-info',
      'Device maintenance': 'fas fa-wrench text-secondary',
      'User login': 'fas fa-user text-success',
      'Configuration changed': 'fas fa-cog text-primary',
      'Stock refilled': 'fas fa-plus-circle text-success',
      'Product updated': 'fas fa-shopping-cart text-info',
      'Error reported': 'fas fa-exclamation-triangle text-danger',
      'System update': 'fas fa-download text-info'
    }

    return iconMap[actionType] || 'fas fa-info-circle text-secondary'
  }

  /**
   * Get display color for different sources
   * @param {string} source - The source of the action
   * @returns {string} Bootstrap color class
   */
  static getSourceColor(source) {
    const colorMap = {
      'touch-console': 'text-primary',
      'web-admin': 'text-success', 
      'webapp': 'text-info',
      'replenish-app': 'text-warning',
      'mobile-app': 'text-warning',
      'system': 'text-secondary',
      'api': 'text-dark'
    }

    return colorMap[source] || 'text-muted'
  }
}

export default MessagesService